/*
  The drop calendar row.

  Three things happen here, all of them desktop-only — on phones the row is a
  plain stack of cards and this file stays out of the way:

  - The colour nearest the middle of the row is the open one. It grows, shows
    its buttons, and the row holds it there.
  - The mouse pointer is replaced by a drawn circle facing the side it would
    travel to. It only appears where it would actually do something: not over
    the open card, which is there to be read rather than clicked past, and not
    pointing at an end the row has already reached. Everywhere it is absent the
    ordinary pointer comes back.
  - 'Get a reminder' does not open a drawer: it carries the visitor down to the
    sign-up further along the page and puts the cursor in the first field.

  With no JavaScript the row is still a horizontal scroller with the colour of
  the month open, and the reminder links are ordinary anchors.
*/
(function () {
  var desktop = window.matchMedia('(min-width: 769px)');
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  function behavior() {
    return reduced.matches ? 'auto' : 'smooth';
  }

  /* How long the row takes to travel from one colour to the next. Paired with
     the card's own timings in monthly-drops-rgd.liquid: the card opens 0.15s
     into the glide and takes 0.6s, so both land together. */
  var GLIDE = 750;

  /* Slow away, quick through the middle, slow to arrive. */
  function ease(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  function init(root) {
    if (root.dataset.mdReady === 'true') return;
    root.dataset.mdReady = 'true';

    var viewport = root.querySelector('[data-md-viewport]');
    var track = root.querySelector('[data-md-track]');
    var cursor = root.querySelector('[data-md-cursor]');
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-md-card]'));
    if (!viewport || !track || !cards.length) return;

    var openIndex = cards.indexOf(root.querySelector('[data-md-current]'));
    if (openIndex < 0) openIndex = 0;

    function centre(el) {
      var box = el.getBoundingClientRect();
      return box.left + box.width / 2;
    }

    /* Where the row would have to be for this card to sit in the middle, as
       the row is laid out right now. */
    function targetFor(card) {
      return track.scrollLeft + centre(card) - centre(track);
    }

    var glide = null;

    function stopGlide() {
      if (!glide) return;
      cancelAnimationFrame(glide.frame);
      glide = null;
      track.classList.remove('is-gliding');
    }

    /*
      The row's own travel, rather than the browser's smooth scroll: that one
      is short, fixed and cannot be tuned, and it reads as a jump.

      The destination is measured again on every frame instead of once at the
      start. The cards either side are opening and closing while the row
      moves, which shifts where the new colour sits — aiming at where it is
      now, not where it was, lets the row arrive exactly on it with no
      correction at the end.
    */
    function centreOn(card, how) {
      stopGlide();

      if (how !== 'smooth') {
        track.scrollLeft = targetFor(card);
        return;
      }

      var from = track.scrollLeft;
      var started = null;
      glide = {};
      track.classList.add('is-gliding');
      /* Put the drawing away now, not a frame from now: the card under the
         pointer has just stopped being the open one. */
      paint();

      function frame(now) {
        if (started === null) started = now;
        var t = Math.min((now - started) / GLIDE, 1);
        track.scrollLeft = from + (targetFor(card) - from) * ease(t);
        /* The row is moving under a pointer that may be holding still. */
        paint();

        if (t < 1) {
          glide.frame = requestAnimationFrame(frame);
        } else {
          glide = null;
          track.classList.remove('is-gliding');
          paint();
        }
      }

      glide.frame = requestAnimationFrame(frame);
    }

    function setOpen(index) {
      if (index === openIndex || !cards[index]) return;

      /* The gaps around the open card grow with it now, so the row is not
         paid back in one go here: whoever opened the card glides the row
         onto it while it grows. */
      if (cards[openIndex]) cards[openIndex].classList.remove('is-open');
      cards[index].classList.add('is-open');
      openIndex = index;

      /* A different card is open now, so the pointer may mean something else
         than it did a moment ago, even if it has not moved. */
      paint();
    }

    function nearest() {
      var middle = centre(track);
      var best = 0;
      var shortest = Infinity;
      for (var i = 0; i < cards.length; i++) {
        var distance = Math.abs(centre(cards[i]) - middle);
        if (distance < shortest) {
          shortest = distance;
          best = i;
        }
      }
      return best;
    }

    /* The card the row is on its way to, while it is still travelling. Until it
       arrives, the middle of the row means nothing: everything in between is
       just a colour passing by. */
    var asked = null;
    var askedClear;

    function hold(index) {
      asked = index;
      clearTimeout(askedClear);
      /* If the row never arrives — an interrupted scroll, a browser that drops
         the animation — stop waiting for it. */
      askedClear = setTimeout(function () {
        asked = null;
      }, 900);
    }

    function step(direction) {
      var next = openIndex + direction;
      if (next < 0) next = 0;
      if (next > cards.length - 1) next = cards.length - 1;
      hold(next);
      setOpen(next);
      centreOn(cards[next], behavior());
    }

    /*
      What a point on the row stands for: -1 to travel left, 1 to travel right,
      0 for nothing at all. Nothing is the answer over the open card and its
      buttons — that card is the one being read — and at either end of the row,
      where there is no colour left to reach that way.
    */
    function reach(x, y) {
      /* The open card is measured by the room it is opening into, not by what
         it covers right now: while it grows, a pointer just below its edge
         would otherwise count as outside it for a moment. Its width never
         changes, and the row is already as tall as an open card. */
      var open = cards[openIndex];
      if (open) {
        var card = open.getBoundingClientRect();
        var row = track.getBoundingClientRect();
        if (x >= card.left && x <= card.right && y >= row.top && y <= row.bottom) return 0;
      }

      var under = document.elementFromPoint(x, y);
      if (under && under.closest) {
        if (under.closest('a, button, input')) return 0;
      }

      var box = viewport.getBoundingClientRect();
      var direction = x < box.left + box.width / 2 ? -1 : 1;
      var next = openIndex + direction;
      if (next < 0 || next > cards.length - 1) return 0;
      return direction;
    }

    /* Where the mouse last was over the row, so the drawing can be redrawn
       when the row moves underneath a hand that is holding still — a
       trackpad swipe leaves the pointer exactly where it was while the open
       card glides in and grows beneath it. Every movement of the row asks
       again: as it glides, as it scrolls, and as a card finishes growing. */
    var pointer = null;

    function paint() {
      if (!cursor || !pointer) return;
      if (!desktop.matches || !finePointer.matches) return;

      /* Nothing to point at while the row is travelling: the colour it is
         leaving was the one being read a moment ago, and the one arriving is
         about to be. The drawing waits for the row to land. */
      var direction = glide ? 0 : reach(pointer.x, pointer.y);
      if (direction) cursor.dataset.direction = direction < 0 ? 'left' : 'right';
      cursor.classList.toggle('is-visible', direction !== 0);
      /* No arrow to draw means the ordinary pointer is the honest one. */
      track.classList.toggle('is-plain', direction === 0);
      /* 58 is half the circle: the drawing sits on the pointer, not beside it. */
      cursor.style.transform =
        'translate3d(' + (pointer.x - 58) + 'px, ' + (pointer.y - 58) + 'px, 0)';
    }

    /* The open card is decided when the scroll settles rather than while it
       moves, so the gaps never shift under a finger. (A trackpad does not come
       through here: its swipes are steps, below.) */
    var settle;
    track.addEventListener(
      'scroll',
      function () {
        if (!desktop.matches) return;
        paint();
        /* The row's own glide already knows where it is going. */
        if (glide) return;
        clearTimeout(settle);
        settle = setTimeout(function () {
          var target = nearest();
          if (asked !== null) {
            /* Mid-flight: hold the card the click asked for rather than
               opening whichever one happens to be passing the middle. */
            if (target !== asked) return;
            asked = null;
          }
          if (target === openIndex) return;
          setOpen(target);
          /* A touchscreen drag leaves the row wherever the hand stopped. Carry
             it the rest of the way onto the colour that just opened, at the
             same pace a click would. */
          centreOn(cards[target], behavior());
        }, 120);
      },
      { passive: true }
    );

    /*
      A sideways swipe on a trackpad is a step, the same as a click: one
      colour per gesture, travelled with the row's own glide. Left to the
      browser, the row would coast freely, snap to a card, and then move a
      second time as that card opened — two movements where there should be
      one.

      A trackpad keeps sending events well after the fingers lift, so a
      gesture is over only once the events have stopped for a moment and the
      glide has landed. Anything that arrives before then belongs to the swipe
      that was already answered. Downward scrolling is left alone: the page
      keeps it.
    */
    var SWIPE = 30;
    var swiped = 0;
    var swipeTaken = false;
    var swipeQuiet;

    track.addEventListener(
      'wheel',
      function (event) {
        if (!desktop.matches) return;

        var dx = event.deltaX;
        /* A mouse wheel with shift held is the sideways scroll of a mouse. */
        if (!dx && event.shiftKey) dx = event.deltaY;
        if (Math.abs(dx) <= Math.abs(event.shiftKey ? 0 : event.deltaY)) return;

        event.preventDefault();
        /* The page's smooth scrolling would otherwise nudge the row too. */
        event.stopPropagation();

        clearTimeout(swipeQuiet);
        swipeQuiet = setTimeout(function release() {
          if (glide) {
            swipeQuiet = setTimeout(release, 60);
            return;
          }
          swiped = 0;
          swipeTaken = false;
        }, 160);

        if (swipeTaken) return;
        swiped += dx;
        if (Math.abs(swiped) < SWIPE) return;

        swipeTaken = true;
        step(swiped > 0 ? 1 : -1);
      },
      { passive: false }
    );

    ['touchstart', 'pointerdown'].forEach(function (name) {
      track.addEventListener(
        name,
        function () {
          asked = null;
          /* A hand on the row takes over from the glide. */
          stopGlide();
        },
        { passive: true }
      );
    });

    viewport.addEventListener('click', function (event) {
      if (!desktop.matches) return;
      var direction = reach(event.clientX, event.clientY);
      if (direction) step(direction);
    });

    track.addEventListener('keydown', function (event) {
      if (!desktop.matches) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        step(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        step(1);
      }
    });

    function follow(event) {
      pointer = { x: event.clientX, y: event.clientY };
      paint();
    }

    /* A card that has just finished opening or closing covers a different
       patch of the row than it did while the pointer was last checked. */
    track.addEventListener('transitionend', function (event) {
      if (event.propertyName === 'height') paint();
    });

    viewport.addEventListener('mouseenter', follow);
    viewport.addEventListener('mousemove', follow);
    viewport.addEventListener('mouseleave', function () {
      pointer = null;
      if (cursor) cursor.classList.remove('is-visible');
      track.classList.remove('is-plain');
    });

    var links = root.querySelectorAll('[data-md-reminder-link]');
    for (var l = 0; l < links.length; l++) {
      links[l].addEventListener('click', function (event) {
        var target = document.querySelector(this.getAttribute('href'));
        if (!target) return;
        event.preventDefault();
        /* Through the page's own smooth scrolling, so the browser and the
           site never pull the page at the same time. */
        var where = { behavior: behavior(), block: 'center' };
        if (window.hogoScrollTo) window.hogoScrollTo(target, where);
        else target.scrollIntoView(where);

        var field = target.querySelector('input:not([type="hidden"])');
        /* Focus without a second scroll, so the page travels once. */
        if (field) field.focus({ preventScroll: true });
      });
    }

    function settleRow() {
      if (!desktop.matches) return;
      hold(openIndex);
      centreOn(cards[openIndex], 'auto');
    }

    var resized;
    window.addEventListener('resize', function () {
      /* Claimed the moment the window moves, not when the row is put back:
         a resize reflows the row immediately, and the scroll it fires would
         otherwise hand the middle — and the open card — to a neighbour before
         this ever runs. */
      hold(openIndex);
      clearTimeout(resized);
      resized = setTimeout(settleRow, 150);
    });

    if (desktop.addEventListener) {
      desktop.addEventListener('change', function () {
        hold(openIndex);
        settleRow();
      });
    }

    settleRow();
  }

  function start() {
    var rows = document.querySelectorAll('[data-monthly-drops]');
    for (var i = 0; i < rows.length; i++) init(rows[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', start);
})();
